import {
  blocksToNodes,
  getImageUrl,
} from '@sanity/block-content-to-hyperscript/internals';
import { h, VNode, Component, DefineComponent } from 'vue';
import {
  NodeChildren,
  Options,
  PortableNode,
  PropsBlock,
  PropsBlockText,
  PropsImage,
  PropsList,
  PropsListItem,
  PropsSpan,
  Serializer,
  SerializerFn,
  Serializers,
  SpanToBeSerialized,
} from './types';

const isVueComponent = (tag: string | Serializer | Component) => {
  return typeof tag !== 'string' && 'setup' in tag;
};

function renderNode(
  serializer: Serializer,
  data?: Record<string, unknown> | null,
  children?: NodeChildren
): VNode {
  // const childNodes = Array.isArray(children) ? children : [children];
  if (typeof serializer === 'function') {
    return (serializer as SerializerFn)({ ...data, children });
  }

  if (isVueComponent(serializer)) {
    if (data) {
      if ('mark' in data && typeof data.mark === 'object') {
        return h(serializer, { ...data.mark }, 'div');
      } else if ('node' in data && typeof data.node === 'object') {
        const sanityProps = { _node: data.node, _options: data.options };
        return h(serializer, { ...data.node, ...sanityProps }, 'div');
      }
    }
    return h(serializer as DefineComponent, {}, 'div');
  }

  return h(serializer, data, children);
}

/* ----------------------------------------
----------------SERIALIZERS----------------
---------------------------------------- */

const BlockSerializer: SerializerFn<PropsBlock> = (props) => {
  const { node, serializers, options, isInline, children } = props;
  const type = node._type;

  const serializer = serializers.types[type] as Serializer;
  if (!serializer) {
    throw new Error(
      `Unknown block type "${type}", please specify a serializer for it in the \`serializers.types\` prop`
    );
  }
  return renderNode(
    serializer,
    {
      node,
      options,
      serializers,
      isInline,
    },
    children
  );
};

// Low-level span serializer
const SpanSerializer: SerializerFn<PropsSpan> = (props) => {
  const { mark, children } = props.node;
  const markType = typeof mark === 'string' ? mark : mark._type;

  const serializer = props.serializers.marks[markType];
  if (!serializer) {
    console.warn(
      `Unknown mark type "${markType}", please specify a serializer for it in the \`serializers.marks\` prop`
    );
    return renderNode(props.serializers.markFallback, null, children);
  }

  return renderNode(serializer, { ...props.node }, children);
};

// Low-level list serializer
const ListSerializer: SerializerFn<PropsList> = (props) => {
  const tag = props.type === 'bullet' ? 'ul' : 'ol';
  return renderNode(tag, null, props.children);
};

// Low-level list item serializer
const ListItemSerializer: SerializerFn<PropsListItem> = (props) => {
  // Don't wrap plain text in paragraphs inside of a list item
  // But wrap any other style in whatever the block serializer says to use
  const children =
    !props.node.style || props.node.style === 'normal'
      ? props.children
      : renderNode(props.serializers.types.block, { ...props }, props.children);
  return renderNode('li', null, children);
};

// Renderer of an actual block of type `block`. Confusing, we know.
const BlockTypeSerializer: SerializerFn<PropsBlockText> = (props) => {
  const style = props.node.style || 'normal';

  if (/^h\d/.test(style)) {
    return renderNode(style, null, props.children);
  }
  // const styleSerializer = props.serializers.styles[style];
  if (style === 'heading') {
    return renderNode('p', { class: 'heading' }, props.children);
  }
  if (style === 'blockquote') {
    return renderNode('blockquote', null, props.children);
  }
  return renderNode('p', null, props.children);
};

const ImageSerializer: SerializerFn<PropsImage> = (props) => {
  const img = renderNode('img', {
    src: getImageUrl(props),
  });

  return props.isInline ? img : renderNode('figure', null, [img]);
};

const serializeSpan = (
  span: SpanToBeSerialized,
  serializers: Serializers,
  index: number,
  options: {
    serializeNode: (
      node: PortableNode,
      index: number,
      siblings: Array<PortableNode>,
      isInline: boolean
    ) => any;
  }
) => {
  if (span === '\n' && serializers.hardBreak) {
    return renderNode(serializers.hardBreak, {
      key: `hb-${index}`,
    });
  }

  if (typeof span === 'string') {
    return serializers.text
      ? renderNode(
          serializers.text,
          {
            key: `text-${index}`,
          },
          [span]
        )
      : span;
  }

  let children;
  if (span.children) {
    children = {
      children: span.children.map((child, i) =>
        options.serializeNode(child, i, span.children, true)
      ),
    };
  }

  const serializedNode = { ...span, ...children };

  return renderNode(serializers.span, {
    key: span._key || `span-${index}`,
    node: serializedNode,
    serializers,
  });
};

const HardBreakSerializer: SerializerFn = () => renderNode('br');

/* ----------------------------------------
-------------------BASIC-------------------
---------------------------------------- */

// Serializers for things that can be directly attributed to a tag without any props
// We use partial application to do this, passing the tag name as the first argument
const RawMarkSerializerFactory = (tag: string) => {
  const RawMarkSerializer: SerializerFn = (props) => {
    return renderNode(tag, null, props.children);
  };
  return RawMarkSerializer;
};

const UnderlineSerializer: SerializerFn = (props) => {
  return renderNode(
    'span',
    {
      style: {
        textDecoration: 'underline',
      },
    },
    props.children
  );
};

const StrikeThroughSerializer: SerializerFn = (props) => {
  return renderNode('del', null, props.children);
};

const LinkSerializer: SerializerFn<{ mark: { href: string } }> = (props) => {
  return renderNode(
    'a',
    {
      href: props.mark.href,
    },
    props.children
  );
};

const ContainerSerializer: SerializerFn = (props) => {
  let properties = null;
  const containerClass = props.className;
  if (containerClass) {
    properties = {
      class: containerClass,
    };
  }
  return renderNode('div', properties, props.children);
};

const defaultSerializers = {
  // Common overrides
  types: {
    block: BlockTypeSerializer,
    image: ImageSerializer,
  },
  marks: {
    strong: RawMarkSerializerFactory('strong'),
    em: RawMarkSerializerFactory('em'),
    code: RawMarkSerializerFactory('code'),
    underline: UnderlineSerializer,
    'strike-through': StrikeThroughSerializer,
    link: LinkSerializer,
  },
  // styles: {},
  list: ListSerializer,
  listItem: ListItemSerializer,
  block: BlockSerializer,
  span: SpanSerializer,
  hardBreak: HardBreakSerializer,
  container: ContainerSerializer,
  markFallback: 'span',
  text: undefined,
  empty: null,
};

export function blockContentToVue(options: Options): any {
  return blocksToNodes(renderNode, options, defaultSerializers, serializeSpan);
}
